// Account Settings Page

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/auth-store';

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function AccountPage() {
  const { user } = useAuthStore();
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const utils = trpc.useUtils();

  // Change password mutation
  const changePasswordMutation = trpc.auth.changePassword.useMutation({
    onSuccess: () => {
      setPasswordSuccess(true);
      resetPasswordForm();
      setTimeout(() => setPasswordSuccess(false), 5000);
    },
  });

  // Enable MFA mutation
  const enableMfaMutation = trpc.auth.enableMfa.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
    },
  });

  // Disable MFA mutation (we'd need to add this endpoint)
  // For now, we'll just show enable MFA

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm<ChangePasswordFormData>();

  const newPassword = watch('newPassword');

  const onChangePassword = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  return (
    <div className="space-y-8">
      {/* Profile Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <div className="font-medium text-gray-900">{user?.email}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Display Name</div>
              <div className="font-medium text-gray-900">{user?.displayName}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Organization</div>
              <div className="font-medium text-gray-900">{user?.tenantName}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Role</div>
              <div className="font-medium text-gray-900 capitalize">
                {user?.role?.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Change Password Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>
        <form onSubmit={handlePasswordSubmit(onChangePassword)} className="space-y-4 max-w-md">
          {passwordSuccess && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700">
              Password changed successfully!
            </div>
          )}

          {changePasswordMutation.error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
              {changePasswordMutation.error.message}
            </div>
          )}

          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              className="input"
              autoComplete="current-password"
              {...registerPassword('currentPassword', {
                required: 'Current password is required',
              })}
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">
                {passwordErrors.currentPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              className="input"
              autoComplete="new-password"
              {...registerPassword('newPassword', {
                required: 'New password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Password must contain uppercase, lowercase, and a number',
                },
              })}
            />
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">
                {passwordErrors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              className="input"
              autoComplete="new-password"
              {...registerPassword('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === newPassword || 'Passwords do not match',
              })}
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">
                {passwordErrors.confirmPassword.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={changePasswordMutation.isPending}
            className="btn-primary"
          >
            {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </section>

      {/* Two-Factor Authentication Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Two-Factor Authentication
        </h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Email-based 2FA</div>
              <p className="text-sm text-gray-500">
                Receive a verification code via email when logging in
              </p>
            </div>
            <div className="text-sm text-gray-500">
              Coming soon
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Two-factor authentication adds an extra layer of security to your account.
          When enabled, you'll need to enter a verification code sent to your email
          each time you log in.
        </p>
      </section>

      {/* Danger Zone */}
      <section>
        <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
        <div className="border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Delete Account</div>
              <p className="text-sm text-gray-500">
                Permanently delete your account and all associated data
              </p>
            </div>
            <button className="btn-secondary text-red-600 border-red-200 hover:bg-red-50" disabled>
              Delete Account
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
